"use client";
import type React from "react";
import { useRef } from "react";
import { createPortal } from "react-dom";
/**
 * Modal Component
 *
 * A reusable modal dialog for displaying content in an overlay.
 * - Uses React Portal to render above all content
 * - Supports custom title, close button, z-index, and width
 * - Handles click outside to close
 * - Accepts children for modal content
 */
import CancelIcon from "@/assets/images/staff-dashboard-cancel-01.svg";
import { Separator } from "@/components/ui/separator";

export interface ModalProps {
  isOpen: boolean;
  title?: React.ReactNode;
  onClose: () => void;
  children?: React.ReactNode;
  zIndex?: number;
  showClose?: boolean;
  closeOnBackdropClick?: boolean;
  width?: string;
  height?: string;
  backdropClassName?: string;
  backdropStyle?: React.CSSProperties;
  innerClassName?: string;
  innerStyle?: React.CSSProperties;
}

const CModal: React.FC<ModalProps> = ({
  isOpen,
  title,
  onClose,
  children,
  zIndex = 999999,
  showClose = true,
  closeOnBackdropClick = false,
  width = "max-w-lg",
  height = "max-h-[90vh]",
  backdropClassName,
  backdropStyle,
  innerClassName,
  innerStyle,
}) => {
  const mouseDownOnBackdrop = useRef(false);

  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop click is a UX convention, not a primary interaction
    <div
      className={`fixed inset-0 flex items-center justify-center p-4 bg-(--color-text-primary)/50 ${backdropClassName || ""}`}
      style={{ zIndex, ...(backdropStyle || {}) }}
      onMouseDown={(e) => {
        if (closeOnBackdropClick)
          mouseDownOnBackdrop.current = e.target === e.currentTarget;
      }}
      onMouseUp={(e) => {
        if (
          closeOnBackdropClick &&
          mouseDownOnBackdrop.current &&
          e.target === e.currentTarget
        ) {
          onClose();
        }
        mouseDownOnBackdrop.current = false;
      }}
    >
      {/* biome-ignore lint/a11y/noStaticElementInteractions: stopPropagation only — no real interaction */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation only — no real interaction */}
      <div
        className={`bg-(--color-bg-white-0) rounded-2xl shadow-2xl w-full ${width} relative ${height} border border-(--color-neutral-200) flex flex-col ${innerClassName || ""}`}
        style={{ zIndex: zIndex + 1, ...(innerStyle || {}) }}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || showClose) && (
          <>
            <div className="flex items-center justify-between px-4 py-4 shrink-0">
              {title &&
                (typeof title === "string" ? (
                  <h3 className="label-lg font-semibold text-(--color-text-primary)">
                    {title}
                  </h3>
                ) : (
                  <div className="label-lg font-semibold text-(--color-text-primary)">
                    {title}
                  </div>
                ))}
              {showClose && (
                <button
                  type="button"
                  onClick={onClose}
                  className="cursor-pointer opacity-60 hover:opacity-100 transition-opacity"
                >
                  <CancelIcon className="size-5" />
                </button>
              )}
            </div>
            <div className="px-4">
              <Separator className="bg-(--color-neutral-200)" />
            </div>
          </>
        )}
        <div className="p-4 overflow-y-auto grow">{children}</div>
      </div>
    </div>,
    document.body,
  );
};

export default CModal;
export { CModal };
